/**
 * Gift Wrap Add-on
 * Automatically adds gift wrapping product to cart when checkbox is checked
 * 
 * @version 1.0.0
 * @author Mood Co
 */

class GiftWrapHandler {
  constructor() {
    this.giftWrapCheckbox = null;
    this.giftWrapVariantId = '56003693773180';
    this.productForm = null;
    this.isProcessing = false;
  }

  /**
   * Initialize the gift wrap handler
   */
  init() {
    try {
      // Find the gift wrap checkbox
      this.giftWrapCheckbox = document.querySelector('[data-gift-wrap-checkbox]');
      if (!this.giftWrapCheckbox) return;

      // Find the product form
      this.productForm = document.querySelector('.product-form form, form[action*="/cart/add"]');
      if (!this.productForm) return;

      // Bind form submission
      this.interceptFormSubmission();
      
      // Bind popup functionality
      this.initPopup();
      
      // Bind message field functionality
      this.initMessageField();
      
    } catch (error) {
      console.error('Error initializing gift wrap:', error);
    }
  }

  /**
   * Initialize message field toggle and character counter
   */
  initMessageField() {
    const messageContainer = document.querySelector('.gift-wrap-message-container');
    const messageTextarea = document.querySelector('[data-gift-message]');
    const charCount = document.querySelector('[data-gift-message-count]');
    
    if (!messageContainer || !messageTextarea) return;

    // Toggle message field when checkbox is checked/unchecked
    this.giftWrapCheckbox.addEventListener('change', () => {
      if (this.giftWrapCheckbox.checked) {
        messageContainer.style.display = 'block';
      } else {
        messageContainer.style.display = 'none';
        messageTextarea.value = ''; // Clear message when unchecked
        if (charCount) charCount.textContent = '0';
      }
    });

    // Character counter
    if (charCount) {
      messageTextarea.addEventListener('input', () => {
        charCount.textContent = messageTextarea.value.length;
      });
    }
  }

  /**
   * Initialize popup functionality
   */
  initPopup() {
    const infoBtn = document.querySelector('[data-gift-wrap-info]');
    if (!infoBtn) return;

    const popup = document.querySelector('[data-gift-wrap-popup]');
    if (!popup) return;

    // Open popup
    infoBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      popup.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    });

    // Close popup
    const closeElements = popup.querySelectorAll('[data-gift-wrap-close]');
    closeElements.forEach(element => {
      element.addEventListener('click', () => {
        this.closePopup(popup);
      });
    });

    // Add gift wrap button - checks the checkbox and closes popup
    const addBtn = popup.querySelector('[data-gift-wrap-add]');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        if (this.giftWrapCheckbox) {
          this.giftWrapCheckbox.checked = true;
        }
        this.closePopup(popup);
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.style.display === 'flex') {
        this.closePopup(popup);
      }
    });
  }

  /**
   * Close popup helper
   */
  closePopup(popup) {
    popup.style.display = 'none';
    document.body.style.overflow = '';
  }

  /**
   * Intercept form submission to add gift wrap if checked
   */
  interceptFormSubmission() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', async (e) => {
      // Only intercept if gift wrap is checked and not already processing
      if (this.giftWrapCheckbox && this.giftWrapCheckbox.checked && !this.isProcessing) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.isProcessing = true;
        
        // Get form data
        const formData = new FormData(this.productForm);
        const items = [{
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity')) || 1
        }];
        
        // Add selling plan if present
        if (formData.get('selling_plan')) {
          items[0].selling_plan = formData.get('selling_plan');
        }
        
        // Add properties if present
        const properties = {};
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.match(/properties\[(.*?)\]/)[1];
            properties[propName] = value;
          }
        }
        if (Object.keys(properties).length > 0) {
          items[0].properties = properties;
        }
        
        // Get gift message if present
        const messageField = document.querySelector('[data-gift-message]');
        const giftMessage = messageField ? messageField.value.trim() : '';
        
        // Add the gift wrap product with message property
        const giftWrapItem = {
          id: this.giftWrapVariantId,
          quantity: 1
        };
        
        if (giftMessage) {
          giftWrapItem.properties = {
            'Gift Message': giftMessage
          };
        }
        
        items.push(giftWrapItem);
        
        try {
          // Add both products to cart
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });
          
          if (!response.ok) {
            throw new Error('Failed to add products to cart');
          }
          
          const result = await response.json();
          
          // Refresh cart UI if available
          if (window.theme && window.theme.cart && typeof window.theme.cart.refresh === 'function') {
            window.theme.cart.refresh();
          }
          
          // Dispatch event for cart updates
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          // Trigger any cart drawer/notification
          const cartDrawer = document.querySelector('cart-drawer, cart-notification');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
          
        } catch (error) {
          console.error('Error adding products to cart:', error);
        } finally {
          this.isProcessing = false;
        }
        
        return false;
      }
    }, true); // Use capture phase to ensure we catch it first
  }
}

/**
 * Auto-initialize when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  window.giftWrapHandler = new GiftWrapHandler();
  window.giftWrapHandler.init();
});

// Also initialize if DOM is already loaded
if (document.readyState !== 'loading') {
  setTimeout(() => {
    if (!window.giftWrapHandler) {
      window.giftWrapHandler = new GiftWrapHandler();
      window.giftWrapHandler.init();
    }
  }, 100);
}
